export const DEFAULT_ERROR = 'Something went wrong.';
export const DEFAULT_GET_ERROR = 'Malformed response.';

export const SERVER_ERRORS = {
  400: '400 Something went wrong.',
  401: 'Your session has expired. Please log in again.',
  403: `You are not authorized to access this. Contact your admin to adjust your permissions.`,
  404: 'Unable to find this resource on the server.',
  409: 'This action did not pass validation.',
  429: 'Too many requests were sent. Please try again shortly.',
  500: '500 Something went wrong.',
};
export function isServerStatusKnown(
  status: number
): status is keyof typeof SERVER_ERRORS {
  return status in SERVER_ERRORS;
}

export type AppErrorKind = 'critical' | 'warning' | 'informative';

export type ApiErrorEntry = {
  message: string;
  kind: AppErrorKind;
  title?: string;
};

export const API_ERRORS = {
  1: {
    message: 'Your company has reached the data usage limit.',
    kind: 'warning',
  },
  4: {
    message: "You don't have permission to access this content.",
    kind: 'warning',
  },
  6: {message: "Your company's license is invalid.", kind: 'warning'},
  7: {message: "Your company's license is expired.", kind: 'warning'},
  10: {
    message: 'This email is already associated with an account.',
    kind: 'warning',
  },
  12: {
    message: 'We were unable to send this inspection for processing.',
    kind: 'critical',
  },
  18: {
    message: 'We were unable to send an invitation to that email.',
    kind: 'critical',
  },
  33: {
    message:
      'The account associated with this email is inactive. Please contact your admin.',
    kind: 'warning',
  },
  37: {message: 'Your company has reached its seat limit.', kind: 'warning'},
  39: {
    message:
      'Unable to be assign user to this asset. Please confirm that they are an active user in your company.',
    kind: 'warning',
  },
  40: {
    message:
      'Unable to be assign user to this asset. Please confirm that they are an active user in your company.',
    kind: 'warning',
  },
  44: {
    message:
      'This category could not be deleted. Please unlink it from all assets to delete it from your library, or delete its markups to delete it from an asset.',
    kind: 'warning',
  },
  46: {message: 'Your company has reached its seat limit.', kind: 'warning'},
  47: {
    message:
      'This category could not be deleted. Please unlink it from all assets to delete it from your library, or delete its markups to delete it from an asset.',
    kind: 'warning',
  },
  48: {
    message:
      'This severity scale could not be deleted. Please unlink it from all assets to delete it from your library, or delete its markups to delete it from an asset.',
    kind: 'warning',
  },
  49: {
    message:
      'You can assign up to 30 categories to an asset, including categories from presets. Remove some categories or presets before assigning more.',
    kind: 'warning',
  },
  51: {
    message:
      'Your company can have up to 10 category presets. Archive an existing preset before creating a new one.',
    kind: 'warning',
  },
  66: {
    message:
      'Please wait until the inspection has finished processing before continuing.',
    kind: 'informative',
  },
  73: {message: 'Unable to find an image at this location.', kind: 'warning'},
  107: {message: 'This capture data already exists.', kind: 'warning'},
  108: {
    message:
      'This inspection does not have enough data. Please add more and try again.',
    kind: 'critical',
  },
  113: {
    message:
      'You are not able to access this inspection. Please contact your admin.',
    kind: 'warning',
  },
  115: {
    message:
      'You are not able to access this inspection. Please contact your admin.',
    kind: 'warning',
  },
  116: {
    message:
      'This view only user has already been claimed. Please check your email for a follow-up.',
    kind: 'informative',
  },
  125: {
    message: 'This tag already exists. Please try another label.',
    kind: 'warning',
  },
  127: {
    message:
      "This inspection cannot be created because the selected images would exceed your company's data limit.",
    kind: 'warning',
  },
  146: {
    message:
      'The chosen categories contain an AI model that is already added to this asset. Duplicated AI models are not permitted.',
    kind: 'warning',
  },
  148: {
    message:
      "This inspection's severity scale is currently being migrated. Please try again in a few minutes.",
    kind: 'informative',
    title: 'Migration in progress',
  },
  149: {
    message: 'This category has been archived and cannot be used.',
    kind: 'warning',
  },
  150: {
    message:
      'Archived severities cannot be assigned to assets. Please restore the severity and try again.',
    kind: 'warning',
  },
  155: {
    message:
      'Could not invite user as a Qii Link for this inspection because they are already a full user.',
    kind: 'warning',
  },
  156: {
    message:
      'This email address already has an active Qii Link for this inspection.',
    kind: 'warning',
  },
  157: {
    message:
      'This link has expired. Please request a new link from the sender.',
    kind: 'warning',
  },
  159: {
    message:
      'This layout cannot be updated because the asset has existing markups.',
    kind: 'warning',
  },
  160: {
    message:
      "This inspection is marked complete and can't be edited. To make changes, contact your account admin.",
    kind: 'informative',
  },
  162: {
    message: 'This asset belongs to a site from another company.',
    kind: 'warning',
  },
  163: {
    message:
      'Location layout migration is currently in progress. Please try again in a few minutes.',
    kind: 'informative',
    title: 'Migration in progress',
  },
  166: {
    message:
      'The location layout for this asset has changed. Complete location segmentation to keep location assignments accurate.',
    kind: 'warning',
    title: 'Location segmentation required',
  },
  167: {
    message:
      'Location segmentation is complete. Existing markups are being reassigned using the updated locations. This inspection will be available once the update is complete.',
    kind: 'informative',
    title: 'Updating markup locations',
  },
} satisfies Record<
  number,
  {message: string; kind: AppErrorKind; title?: string}
>;

export function isApiCodeKnown(code: number): code is keyof typeof API_ERRORS {
  return code in API_ERRORS;
}

export const SENTRY_WHITELIST = [12, 18, 108];

export class AppError extends Error {
  constructor(
    message: string,
    public readonly kind: AppErrorKind = 'critical',
    public readonly title?: string,
    public readonly code?: number
  ) {
    super(message);
    this.name = 'AppError';
  }
}
